/**
 * Jixxeo Guess My Number — each player secretly picks a number 1-100.
 * Players take turns guessing. Server relays guesses. First correct guess wins.
 * If opponent responds with "hotter/colder" (auto from their local pick),
 * that hint is shown.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MpShell } from "./_mpShell";
import { useMpClient } from "./_mpClient";

export default function JixxeoGuessNumber() {
  const loc = useLocation();
  const challenge = new URLSearchParams(loc.search).get("challenge");
  const [stake, setStake] = useState(0);
  const [started, setStarted] = useState(!!challenge);
  const [key, setKey] = useState(0);

  const [mySecret, setMySecret] = useState("");
  const [locked, setLocked] = useState(false);
  const [myTurn, setMyTurn] = useState(false);
  const [myGuess, setMyGuess] = useState("");
  const [history, setHistory] = useState([]);   // {by, guess, hint}
  const [opponentReady, setOpponentReady] = useState(false);

  const client = useMpClient({ game: "guess-number", stake, challenge, autoConnect: started });

  useEffect(() => { setHistory([]); setMySecret(""); setLocked(false); setOpponentReady(false); }, [key]);
  useEffect(() => {
    if (client.status === "paired") setMyTurn(!!client.youFirst);
  }, [client.status, client.youFirst]);

  // Incoming opponent moves
  useEffect(() => {
    if (!client.lastMove) return;
    const m = client.lastMove.move || {};
    if (m.type === "ready") { setOpponentReady(true); return; }
    if (m.type === "guess") {
      // opponent guessed; compute hint
      const g = Number(m.guess);
      const secretN = Number(mySecret);
      let hint = "cold";
      if (g === secretN) hint = "correct";
      else {
        const d = Math.abs(g - secretN);
        hint = d <= 3 ? "🔥 boiling" : d <= 8 ? "warm" : d <= 20 ? "cool" : "cold";
      }
      setHistory((h) => [...h, { by: "opp", guess: g, hint }]);
      client.sendMove({ type: "hint", guess: g, hint });
      if (hint === "correct") client.sendResult("lose");
      else setMyTurn(true);
      return;
    }
    if (m.type === "hint") {
      // update our history for our previous guess
      setHistory((h) => h.map((row, i) => i === h.length - 1 && row.by === "me" ? { ...row, hint: m.hint } : row));
      if (m.hint === "correct") client.sendResult("win");
      return;
    }
  }, [client.lastMove, mySecret]); // eslint-disable-line

  const lockSecret = () => {
    const n = Number(mySecret);
    if (!(n >= 1 && n <= 100)) return;
    setLocked(true);
    client.sendMove({ type: "ready" });
  };

  const submitGuess = () => {
    if (!myTurn || !locked || !opponentReady) return;
    const g = Number(myGuess);
    if (!(g >= 1 && g <= 100)) return;
    setHistory((h) => [...h, { by: "me", guess: g, hint: "…" }]);
    client.sendMove({ type: "guess", guess: g });
    setMyTurn(false);
    setMyGuess("");
  };

  const canGuess = myTurn && locked && opponentReady && client.status === "paired";

  return (
    <MpShell title="Jixxeo Guess Number" accent="#39FF14" game="guess-number" gameSlug="jixxeo-guess-number"
      status={started ? client.status : "idle"}
      self={client.self} opponent={client.opponent}
      settlement={client.settlement} stake={stake} setStake={setStake}
      onFindOpponent={() => { setStarted(true); setTimeout(() => client.connect(), 0); }}
      onRematch={() => { client.close(); setKey((k) => k + 1); setStarted(false); setTimeout(() => { setStarted(true); client.connect(); }, 100); }}
      initialChallenge={challenge}>
      <div data-testid="guess-body" className="space-y-4">
        {!locked ? (
          <div className="text-center space-y-2">
            <div className="mono text-[10px] uppercase tracking-widest text-white/60">Pick your secret number (1-100)</div>
            <input data-testid="guess-secret-input" type="number" min={1} max={100}
              value={mySecret} onChange={(e) => setMySecret(e.target.value.replace(/[^\d]/g, ""))}
              className="w-full bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-center text-2xl font-black" />
            <button data-testid="guess-lock" onClick={lockSecret}
              disabled={!(Number(mySecret) >= 1 && Number(mySecret) <= 100)}
              className="w-full py-2 rounded-full font-black mono text-xs uppercase tracking-widest bg-[#39FF14] text-black disabled:opacity-40">
              Lock in
            </button>
          </div>
        ) : (
          <>
            <div className="mono text-[10px] uppercase tracking-widest text-center text-white/60">
              {!opponentReady ? "Waiting for opponent to lock…" : myTurn ? "Your guess (1-100)" : "Opponent's turn…"}
            </div>
            {opponentReady && (
              <div className="flex gap-2">
                <input data-testid="guess-input" type="number" min={1} max={100}
                  disabled={!canGuess}
                  value={myGuess} onChange={(e) => setMyGuess(e.target.value.replace(/[^\d]/g, ""))}
                  className="flex-1 bg-black/60 border border-white/10 rounded-xl px-3 py-2 text-center text-lg font-black" />
                <button data-testid="guess-submit" onClick={submitGuess}
                  disabled={!canGuess}
                  className="px-4 rounded-xl font-black mono text-xs uppercase tracking-widest bg-[#39FF14] text-black disabled:opacity-40">
                  Guess
                </button>
              </div>
            )}
            <div className="max-h-52 overflow-y-auto space-y-1">
              {history.map((row, i) => (
                <div key={i} className="flex items-center justify-between mono text-xs px-2 py-1 rounded-lg border border-white/10 bg-white/5">
                  <span className="text-white/60">{row.by === "me" ? "You" : "Opp"}</span>
                  <span className="font-black text-white">{row.guess}</span>
                  <span className="text-[#FFE300]">{row.hint}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </MpShell>
  );
}
